/**
 * Названия уровней просмотра для интерфейса.
 *
 * `lib/levels.ts` читают node-скрипты, поэтому русские подписи там остаются
 * как есть, а то, что видит человек, переводится здесь — по коду уровня.
 */
import { defineMessages } from "../../i18n/define";
import { tr } from "../../i18n/runtime";
import type { LevelId } from "../../lib/levels";

const m = defineMessages({
  district: {
    id: "answer.level.district",
    defaultMessage: "район",
    description: "Название уровня просмотра «район» строчными, вставляется в предложение (uz: tuman)",
  },
  massif: {
    id: "answer.level.massif",
    defaultMessage: "массив",
    description: "Название уровня просмотра «массив» (группа хозяйств и земель) строчными, вставляется в предложение",
  },
  actor: {
    id: "answer.level.actor",
    defaultMessage: "хозяйство",
    description: "Название уровня просмотра «хозяйство» строчными, вставляется в предложение",
  },
  contour: {
    id: "answer.level.contour",
    defaultMessage: "контур",
    description: "Название уровня просмотра «контур» (участок земли по кадастру) строчными, вставляется в предложение",
  },
  work: {
    id: "answer.level.work",
    defaultMessage: "работа",
    description: "Название уровня просмотра «работа» (агроприём на поле) строчными, вставляется в предложение",
  },
  districtTitle: {
    id: "answer.level.district.title",
    defaultMessage: "Район",
    description: "Название уровня просмотра «район» с заглавной буквы: заголовок карточки уровня",
  },
  massifTitle: {
    id: "answer.level.massif.title",
    defaultMessage: "Массив",
    description: "Название уровня просмотра «массив» с заглавной буквы: заголовок карточки уровня",
  },
  actorTitle: {
    id: "answer.level.actor.title",
    defaultMessage: "Хозяйство",
    description: "Название уровня просмотра «хозяйство» с заглавной буквы: заголовок карточки уровня",
  },
  contourTitle: {
    id: "answer.level.contour.title",
    defaultMessage: "Контур",
    description: "Название уровня просмотра «контур» с заглавной буквы: заголовок карточки уровня",
  },
  workTitle: {
    id: "answer.level.work.title",
    defaultMessage: "Работа",
    description: "Название уровня просмотра «работа» с заглавной буквы: заголовок карточки уровня",
  },
});

/** Уровень строчными, для середины предложения. */
export function levelWord(level: LevelId): string {
  return tr(m[level]);
}

/** Уровень с заглавной, для заголовка. */
export function levelTitle(level: LevelId): string {
  return tr(m[`${level}Title` as const]);
}

const denominators = defineMessages({
  districtMassifs: { id: "answer.denominator.massifs", defaultMessage: "массивы", description: "В чём считается доля на уровне «район»: массивы; строчными, в списке через запятую" },
  districtContours: { id: "answer.denominator.contours", defaultMessage: "контуры", description: "В чём считается доля на уровне «район»: контуры (участки земли); строчными, в списке" },
  districtFarms: { id: "answer.denominator.farms", defaultMessage: "хозяйства", description: "В чём считается доля на уровне «район»: хозяйства; строчными, в списке" },
  districtHectares: { id: "answer.denominator.districtHectares", defaultMessage: "гектары района", description: "В чём считается доля на уровне «район»: гектары всего района; строчными, в списке" },
  districtWorks: { id: "answer.denominator.districtWorks", defaultMessage: "работы на полях района", description: "В чём считается доля на уровне «район»: работы на полях района (поручения работ полям); строчными, в списке" },
  massifContours: { id: "answer.denominator.massifContours", defaultMessage: "контуры массива", description: "В чём считается доля на уровне «массив»: контуры массива; строчными, в списке" },
  massifFarms: { id: "answer.denominator.massifFarms", defaultMessage: "хозяйства массива", description: "В чём считается доля на уровне «массив»: хозяйства массива; строчными, в списке" },
  massifHectares: { id: "answer.denominator.massifHectares", defaultMessage: "гектары массива", description: "В чём считается доля на уровне «массив»: гектары массива; строчными, в списке" },
  massifWorks: { id: "answer.denominator.massifWorks", defaultMessage: "работы на полях массива", description: "В чём считается доля на уровне «массив»: работы на полях массива; строчными, в списке" },
  farmContours: { id: "answer.denominator.farmContours", defaultMessage: "контуры хозяйства", description: "В чём считается доля на уровне «хозяйство»: контуры хозяйства; строчными, в списке" },
  farmWorks: { id: "answer.denominator.farmWorks", defaultMessage: "работы на полях хозяйства", description: "В чём считается доля на уровне «хозяйство»: работы на полях хозяйства; строчными, в списке" },
  farmHectares: { id: "answer.denominator.farmHectares", defaultMessage: "гектары хозяйства", description: "В чём считается доля на уровне «хозяйство»: гектары хозяйства; строчными, в списке" },
  contourWorks: { id: "answer.denominator.contourWorks", defaultMessage: "работы на контуре", description: "В чём считается доля на уровне «контур»: работы на этом контуре; строчными, в списке" },
  contourParts: { id: "answer.denominator.contourParts", defaultMessage: "части контура", description: "В чём считается доля на уровне «контур»: части контура; строчными, в списке" },
  contourHectares: { id: "answer.denominator.contourHectares", defaultMessage: "гектары контура", description: "В чём считается доля на уровне «контур»: гектары контура; строчными, в списке" },
  workContours: { id: "answer.denominator.workContours", defaultMessage: "контуры работы", description: "В чём считается доля на уровне «работа»: контуры, на которых идёт работа; строчными, в списке" },
  workObligations: { id: "answer.denominator.workObligations", defaultMessage: "работы на полях по этой работе", description: "В чём считается доля на уровне «работа»: поручения этой работы полям; строчными, в списке" },
});

/** Служебные имена знаменателей (`lib/levels.ts`) → слова. Незнакомое печатается как есть. */
const DENOMINATOR_KEYS: Record<string, keyof typeof denominators> = {
  "массивы": "districtMassifs", // i18n-ignore: служебный ключ знаменателя из lib/levels.ts
  "контуры": "districtContours", // i18n-ignore: служебный ключ знаменателя из lib/levels.ts
  "хозяйства": "districtFarms", // i18n-ignore: служебный ключ знаменателя из lib/levels.ts
  "гектары района": "districtHectares", // i18n-ignore: служебный ключ знаменателя из lib/levels.ts
  "обязательства района": "districtWorks", // i18n-ignore: служебный ключ знаменателя из lib/levels.ts
  "контуры массива": "massifContours", // i18n-ignore: служебный ключ знаменателя из lib/levels.ts
  "хозяйства массива": "massifFarms", // i18n-ignore: служебный ключ знаменателя из lib/levels.ts
  "гектары массива": "massifHectares", // i18n-ignore: служебный ключ знаменателя из lib/levels.ts
  "обязательства массива": "massifWorks", // i18n-ignore: служебный ключ знаменателя из lib/levels.ts
  "контуры хозяйства": "farmContours", // i18n-ignore: служебный ключ знаменателя из lib/levels.ts
  "обязательства хозяйства": "farmWorks", // i18n-ignore: служебный ключ знаменателя из lib/levels.ts
  "гектары хозяйства": "farmHectares", // i18n-ignore: служебный ключ знаменателя из lib/levels.ts
  "работы на контуре": "contourWorks", // i18n-ignore: служебный ключ знаменателя из lib/levels.ts
  "части контура": "contourParts", // i18n-ignore: служебный ключ знаменателя из lib/levels.ts
  "гектары контура": "contourHectares", // i18n-ignore: служебный ключ знаменателя из lib/levels.ts
  "контуры работы": "workContours", // i18n-ignore: служебный ключ знаменателя из lib/levels.ts
  "обязательства работы": "workObligations", // i18n-ignore: служебный ключ знаменателя из lib/levels.ts
};

export function denominatorWord(code: string): string {
  const key = DENOMINATOR_KEYS[code];
  return key ? tr(denominators[key]) : code;
}
